import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Crown, Loader2, Copy, Trash2, Eye, CheckCircle2, Play, Pencil, X, ImagePlus } from "lucide-react";

export default function AthleteInvitePanel() {
  const [invites, setInvites] = useState([]);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [card, setCard] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pool, setPool] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/admin/athlete-invites").then(({ data }) => setInvites(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openPicker = () => {
    setPickerOpen(true);
    if (!pool) api.get("/admin/pool/cards").then(({ data }) => setPool([...(data.live || []), ...(data.denied || [])])).catch(() => setPool([]));
  };

  const resetForm = () => { setName(""); setNote(""); setMessage(""); setCard(null); setEditing(null); };

  const save = async () => {
    setBusy(true);
    try {
      const body = { name, note, message, card_id: card?.id || "" };
      if (editing) {
        await api.put(`/admin/athlete-invites/${editing}`, body);
        toast.success("Invite updated");
      } else {
        const { data } = await api.post("/admin/athlete-invites", body);
        copyLink(data);
      }
      resetForm();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const edit = (inv) => {
    setEditing(inv.id); setName(inv.name); setNote(inv.note); setMessage(inv.message || "");
    setCard(inv.card_id ? { id: inv.card_id, url: null } : null);
    if (inv.card_id && !pool) api.get("/admin/pool/cards").then(({ data }) => {
      const all = [...(data.live || []), ...(data.denied || [])];
      setPool(all);
      const c = all.find((x) => x.id === inv.card_id);
      if (c) setCard(c);
    }).catch(() => {});
    else if (inv.card_id && pool) {
      const c = pool.find((x) => x.id === inv.card_id);
      if (c) setCard(c);
    }
  };

  const copyLink = (inv) => {
    const url = `${window.location.origin}/api/i/${inv.token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success(`${inv.name}'s classified invite link copied — texting it shows a CLASSIFIED dossier preview, not a generic web card`),
      () => toast.info(url));
  };

  const preview = (inv) => window.open(`/invite/${inv.token}?preview=1`, "_blank");

  const remove = async (inv) => {
    try { await api.delete(`/admin/athlete-invites/${inv.id}`); toast.success(`${inv.name}'s invite revoked — the link is dead`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = (pool || []).filter((c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="border border-pb-border bg-pb-surface p-5" data-testid="athlete-invite-panel">
      <div className="flex items-center gap-2 mb-1"><Crown className="w-4 h-4 text-yellow-400" /><span className="font-display font-bold uppercase">Founding Athlete Invites</span></div>
      <p className="text-xs text-zinc-500 mb-4">
        Each athlete gets a private cinematic invite: a futuristic scroll unrolls, their message laser-etches in
        calculator digits, their forged card digitally materializes, and "Yes, I'm In" reveals the full pitch in
        matrix rain. Use <b className="text-teal-300">Preview</b> to watch it exactly as they will before you send the link.
      </p>

      <div className="border border-yellow-500/30 bg-yellow-500/5 p-3 mb-4 space-y-3" data-testid="invite-create-form">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Athlete name</label>
            <input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
              placeholder="e.g. Anna Leigh Waters"
              className="w-full bg-pb-elevated border border-pb-border px-2.5 py-2 text-xs text-white focus:outline-none focus:border-yellow-400" />
          </div>
          <div className="flex-[2] min-w-[200px]">
            <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Personal note (shows on the pitch page, optional)</label>
            <input data-testid="invite-note-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
              placeholder='e.g. "Loved your run at the Open — your card would be a chase card day one."'
              className="w-full bg-pb-elevated border border-pb-border px-2.5 py-2 text-xs text-white focus:outline-none focus:border-yellow-400" />
          </div>
        </div>
        <div>
          <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">
            Scroll message — laser-etched character by character ({message.length}/600 · leave blank for the default summons)
          </label>
          <textarea data-testid="invite-message-input" value={message} onChange={(e) => setMessage(e.target.value.slice(0, 600))} rows={4}
            placeholder={"NAME, THE BULLPEN HAS SELECTED YOU.\nYOUR OFFICIAL PLAYER CARD HAS BEEN FORGED AND AWAITS YOUR APPROVAL..."}
            className="w-full bg-pb-elevated border border-pb-border px-2.5 py-2 text-xs text-white font-mono focus:outline-none focus:border-yellow-400" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="invite-pick-card-btn" onClick={openPicker}
            className="flex items-center gap-2 border border-teal-400/50 text-teal-300 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest hover:bg-teal-400/10">
            <ImagePlus className="w-3.5 h-3.5" /> {card ? "Change card" : "Attach their card"}
          </button>
          {card && (
            <div className="flex items-center gap-2" data-testid="invite-picked-card">
              {card.url && <img src={card.url} alt="" className="w-8 h-11 object-cover border border-teal-400/40" />}
              <span className="text-[10px] text-zinc-300">{card.name || card.id}</span>
              <button data-testid="invite-clear-card" onClick={() => setCard(null)} className="text-zinc-600 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <div className="ml-auto flex gap-2">
            {editing && (
              <button data-testid="invite-cancel-edit" onClick={resetForm}
                className="border border-pb-border text-zinc-400 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest hover:text-white">Cancel</button>
            )}
            <button data-testid="invite-create-btn" onClick={save} disabled={!name.trim() || busy}
              className="flex items-center gap-1.5 bg-yellow-400 text-black font-black uppercase tracking-widest px-5 py-2.5 text-xs hover:bg-yellow-300 disabled:opacity-40">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : editing ? <Pencil className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {editing ? "Save Changes" : "Create & Copy Link"}
            </button>
          </div>
        </div>
      </div>

      <div className="divide-y divide-pb-border/40">
        {invites.map((inv) => (
          <div key={inv.id} className="py-2.5 flex flex-wrap items-center gap-2.5" data-testid={`invite-row-${inv.id}`}>
            <div className="flex-1 min-w-[140px]">
              <div className="text-sm font-bold flex items-center gap-2">
                {inv.name}
                {inv.card_id && <span className="text-[8px] font-black uppercase tracking-widest border border-teal-400/50 text-teal-300 px-1.5 py-0.5">card attached</span>}
                {inv.interested && (
                  <span className="flex items-center gap-1 border border-emerald-500 text-emerald-400 text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5" data-testid={`invite-interested-${inv.id}`}>
                    <CheckCircle2 className="w-2.5 h-2.5" /> I'm In
                  </span>
                )}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">
                <Eye className="w-2.5 h-2.5 inline mr-1 -mt-0.5" />{inv.views} view{inv.views === 1 ? "" : "s"}
                {inv.last_viewed ? ` · last ${String(inv.last_viewed).slice(0, 10)}` : ""} · created {String(inv.created_at || "").slice(0, 10)}
              </div>
            </div>
            <button data-testid={`invite-preview-${inv.id}`} onClick={() => preview(inv)}
              className="flex items-center gap-1.5 border border-teal-400/60 text-teal-300 px-3 py-2 text-[9px] font-black uppercase tracking-widest hover:bg-teal-400 hover:text-black transition-colors">
              <Play className="w-3 h-3" /> Preview
            </button>
            <button data-testid={`invite-copy-${inv.id}`} onClick={() => copyLink(inv)}
              className="flex items-center gap-1.5 border border-pb-border text-zinc-300 px-3 py-2 text-[9px] font-black uppercase tracking-widest hover:border-yellow-400 hover:text-yellow-300 transition-colors">
              <Copy className="w-3 h-3" /> Copy Link
            </button>
            <button data-testid={`invite-edit-${inv.id}`} onClick={() => edit(inv)}
              className="p-2 border border-pb-border text-zinc-500 hover:border-teal-400 hover:text-teal-300 transition-colors">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button data-testid={`invite-delete-${inv.id}`} onClick={() => remove(inv)}
              className="p-2 border border-pb-border text-zinc-500 hover:border-red-500 hover:text-red-400 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {invites.length === 0 && <div className="py-4 text-xs text-zinc-600">No invites yet — create your first above.</div>}
      </div>

      {pickerOpen && (
        <div className="fixed inset-0 z-[95] bg-black/90 overflow-y-auto p-4" data-testid="invite-card-picker">
          <div className="max-w-3xl mx-auto bg-pb-surface border border-teal-400/40 p-4 my-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-display font-bold uppercase text-sm">Pick the card you forged for them</span>
              <input data-testid="invite-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…"
                className="flex-1 bg-pb-elevated border border-pb-border px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-teal-400" />
              <button data-testid="invite-picker-close" onClick={() => setPickerOpen(false)}><X className="w-5 h-5 text-zinc-500 hover:text-white" /></button>
            </div>
            {!pool ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-teal-300 animate-spin" /></div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5 max-h-[60vh] overflow-y-auto pr-1">
                {filtered.map((c) => (
                  <button key={c.id} data-testid={`invite-pick-${c.id}`} onClick={() => { setCard(c); setPickerOpen(false); }}
                    className="border border-pb-border hover:border-teal-400 p-1 text-left transition-colors">
                    <img src={c.url} alt={c.name} loading="lazy" className="w-full aspect-[3/4] object-contain bg-black" />
                    <div className="text-[9px] font-bold truncate mt-1">{c.name}</div>
                  </button>
                ))}
                {filtered.length === 0 && <div className="col-span-full text-xs text-zinc-600 py-6 text-center">No cards match.</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
